import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useEffectOnce, useLocalStorage, useUpdateEffect } from 'react-use';
import DataViewTable from 'talent_matching/components/table/data_view_table';
import NoOverflowFullWidth from 'talent_matching/components/shared/no_overflow_full_width';
import { TalentMatchingFilters, Row } from 'talent_matching/types';
import { usePeople } from 'talent_matching/hooks/use_people';
import { useUserContext } from 'talent_matching/contexts/user_context';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import useSelectionsHelper from 'talent_matching/hooks/use_selections_helper';
import { useRankingInProgress } from 'talent_matching/contexts/ranking_in_progress_context';
import { useIndexingStatus } from 'talent_matching/contexts/indexing_status_context';
import {
  GridRowSelectionModel,
  GridColumnVisibilityModel,
} from '@mui/x-data-grid';
import {
  ACTIONS_COLUMN_FIELD,
  DEFAULT_COLUMN_VISIBILITY_MODEL,
} from 'talent_matching/components/table/column_visibility_defaults';

const useColumnVisibility = (
  initialColumns: GridColumnVisibilityModel
): [
  GridColumnVisibilityModel | undefined,
  (model: GridColumnVisibilityModel) => void
] => {
  const { canViewTalentMatchingAiMatchScore } = useUserContext();

  const [talentMatchingActive, setTalentMatchingActive] = useLocalStorage(
    'talent-matching-active',
    canViewTalentMatchingAiMatchScore
  );

  const [columnVisibilityModel, setStoredColumnVisibilityModel] =
    useLocalStorage<GridColumnVisibilityModel>(
      'talent-matching-column-visibility',
      initialColumns
    );

  useEffectOnce(() => {
    if (canViewTalentMatchingAiMatchScore && !talentMatchingActive) {
      setStoredColumnVisibilityModel(initialColumns);
    }
    setTalentMatchingActive(canViewTalentMatchingAiMatchScore);
  });

  const normalizedColumnVisibilityModel = useMemo(() => {
    if (!columnVisibilityModel) {
      return columnVisibilityModel;
    }

    return {
      ...initialColumns,
      ...columnVisibilityModel,
      fullName: true,
      [ACTIONS_COLUMN_FIELD]: true,
    };
  }, [columnVisibilityModel, initialColumns]);

  const setColumnVisibilityModel = (model: GridColumnVisibilityModel) => {
    setStoredColumnVisibilityModel({
      ...initialColumns,
      ...model,
      fullName: true,
      [ACTIONS_COLUMN_FIELD]: true,
    });
  };

  return [normalizedColumnVisibilityModel, setColumnVisibilityModel];
};

const TableWrapper = ({ isActive = true }: { isActive?: boolean }) => {
  // Track when tab becomes active to force table remount (fixes column header sizing)
  const [mountKey, setMountKey] = useState(0);
  const previousIsActive = useRef(isActive);

  useEffect(() => {
    if (!previousIsActive.current && isActive) {
      // Tab just became active - increment key to force remount
      setMountKey((k) => k + 1);
    }
    previousIsActive.current = isActive;
  }, [isActive]);

  const { watch, getValues, setValue } =
    useFormContext<TalentMatchingFilters>();
  const rowsPerPage = getValues('perPage');
  const currentPage = watch('page');
  const filterNames = Object.keys(getValues());
  const chosenFilterNames = filterNames.filter(
    (name) => name !== 'page' && name !== 'excludedIds'
  ) as (keyof TalentMatchingFilters)[];
  const chosenFilters = watch(chosenFilterNames);
  const jsonChosenFilters = JSON.stringify(chosenFilters);

  const [preSelectedRow, setPreSelectedRow] = useState<Row | null>(null);

  const onPageChange = (newPage: number) => setValue('page', newPage + 1);

  const {
    data: tableData,
    refetch: refetchPeople,
    isLoading,
    isFetching,
    isPlaceholderData,
  } = usePeople();
  const rows = tableData?.rows || [];
  const totalRows = tableData?.totalRows || 0;
  const selectionsHelper = useSelectionsHelper(rows, totalRows);

  const { setPeopleFromSelections, removeSelections } = selectionsHelper;

  const onRowSelected = (selectedRows: GridRowSelectionModel) => {
    setPeopleFromSelections(selectedRows);
  };

  useEffect(() => {
    removeSelections();
  }, [mountKey, currentPage, removeSelections]);

  useEffect(() => {
    setValue('page', 1);
  }, [jsonChosenFilters, setValue]);

  useEffect(() => {
    if (totalRows < currentPage - 1 * rowsPerPage) {
      setValue('page', 1);
    }
  }, [currentPage, rowsPerPage, totalRows, setValue]);

  const { rankingInProgress } = useRankingInProgress();
  const { indexingInProgress } = useIndexingStatus();

  useUpdateEffect(() => {
    if (!rankingInProgress) {
      refetchPeople();
    }
  }, [rankingInProgress, refetchPeople]);

  useUpdateEffect(() => {
    if (!indexingInProgress) {
      refetchPeople();
    }
  }, [indexingInProgress, refetchPeople]);

  const tableLoading = isLoading || isFetching || isPlaceholderData;

  const [columnVisibilityModel, setColumnVisibilityModel] = useColumnVisibility(
    DEFAULT_COLUMN_VISIBILITY_MODEL
  );

  return (
    <NoOverflowFullWidth>
      <DataViewTable
        key={mountKey}
        rows={rows}
        totalRows={totalRows}
        loading={tableLoading}
        rowsPerPage={rowsPerPage}
        currentPage={currentPage}
        onPageChange={onPageChange}
        onRowSelected={onRowSelected}
        selectionsHelper={selectionsHelper}
        columnVisibilityModel={
          columnVisibilityModel as GridColumnVisibilityModel
        }
        setColumnVisibilityModel={setColumnVisibilityModel}
        preSelectedRow={preSelectedRow}
        setPreSelectedRow={setPreSelectedRow}
      />
    </NoOverflowFullWidth>
  );
};

export default TableWrapper;
